<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { standardDateTime } from 'shared/helpers/timeHelper';
import { frontendURL } from 'dashboard/helper/URLHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SegmentedControl from 'dashboard/components-next/segmented-control/SegmentedControl.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { META_WHATSAPP_PRICE_USD } from 'dashboard/constants/campaigns';

const props = defineProps({
  campaign: {
    type: Object,
    default: null,
  },
});

// Sent explicitly rather than left to the server's default, so the count shown here and the count
// actually retried cannot drift apart. Campaigns::RetryFailedSendsService clamps it to 1..500.
const RETRY_BATCH_SIZE = 100;

const { t, te } = useI18n();
const store = useStore();
const route = useRoute();

const conversationUrl = displayId =>
  frontendURL(`accounts/${route.params.accountId}/conversations/${displayId}`);

const dialogRef = ref(null);
const isLoading = ref(false);
const sends = ref([]);
const currentPage = ref(1);
const statusFilter = ref('');
const meta = reactive({ totalEntries: 0, perPage: 25 });
const summary = ref({});
const pricingSummary = ref({});
const engagementSummary = ref({});
const failedErrorSummary = ref({});
const clickedButtonSummary = ref({});
const deliveryFailedCount = ref(0);
const retryableCount = ref(0);
const retryDialogRef = ref(null);
const isRetrying = ref(false);
const pricePerCategory = reactive({ ...META_WHATSAPP_PRICE_USD });

const statusOptions = computed(() => [
  { value: '', label: t('CAMPAIGN.SENDS.FILTER.ALL') },
  { value: 'sent', label: t('CAMPAIGN.SENDS.FILTER.SENT') },
  { value: 'failed', label: t('CAMPAIGN.SENDS.FILTER.FAILED') },
  { value: 'skipped', label: t('CAMPAIGN.SENDS.FILTER.SKIPPED') },
]);

const tableHeaders = computed(() => [
  t('CAMPAIGN.SENDS.TABLE.CONTACT'),
  t('CAMPAIGN.SENDS.TABLE.PHONE_NUMBER'),
  t('CAMPAIGN.SENDS.TABLE.STATUS'),
  t('CAMPAIGN.SENDS.TABLE.ERROR'),
  t('CAMPAIGN.SENDS.TABLE.SENT_AT'),
]);

const statusBadgeClass = status =>
  ({
    sent: 'bg-n-teal-3 text-n-teal-11',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    delivery_failed: 'bg-n-ruby-3 text-n-ruby-11',
    skipped: 'bg-n-slate-3 text-n-slate-11',
  })[status] || 'bg-n-slate-3 text-n-slate-11';

// `status` on a campaign_send is set once at send time and never revisited - a message that
// reaches WhatsApp but gets rejected by the provider (payment issue, invalid template...) stays
// `sent` forever, since that rejection only arrives later via an async status webhook (see
// Campaigns::RetryFailedSendsService for the full explanation). `delivery_failed_at` is the only
// place that failure shows up, so the table needs its own status for display purposes.
const rowStatus = send =>
  send.status === 'sent' && send.delivery_failed_at
    ? 'delivery_failed'
    : send.status;

const rowStatusLabel = send =>
  rowStatus(send) === 'delivery_failed'
    ? t('CAMPAIGN.SENDS.TABLE.DELIVERY_FAILED_LABEL')
    : t(`CAMPAIGN.SENDS.FILTER.${send.status.toUpperCase()}`);

const pricingCategories = computed(() =>
  Object.keys(pricingSummary.value).filter(
    category => pricingSummary.value[category] > 0
  )
);

const hasPricingSummary = computed(() => pricingCategories.value.length > 0);

const estimatedTotal = computed(() =>
  pricingCategories.value.reduce((total, category) => {
    const price = Number(pricePerCategory[category]) || 0;
    return total + price * pricingSummary.value[category];
  }, 0)
);

// Rates are all relative to `sent` (not chained delivered->read->clicked) so a metric with
// zero denominator just reads 0% instead of cascading NaN/blank into the ones after it.
const sentTotal = computed(() => engagementSummary.value.sent || 0);

const engagementRate = key => {
  if (!sentTotal.value) return 0;
  return Math.round(
    ((engagementSummary.value[key] || 0) / sentTotal.value) * 100
  );
};

const hasEngagementSummary = computed(() => sentTotal.value > 0);

// Meta's delivery-status error only carries a stable `code` for the Cloud API - translate it via
// the same map used for chat message errors (CHAT_LIST.WHATSAPP_ERROR_CODES), falling back to
// whatever raw text the provider/campaign job recorded.
const translatedDeliveryError = code => {
  const key = `CHAT_LIST.WHATSAPP_ERROR_CODES.${code}`;
  return code && te(key) ? t(key) : null;
};

const rowError = send =>
  send.error_message ||
  translatedDeliveryError(send.delivery_error_code) ||
  send.delivery_error;

const sortedEntries = summaryObject =>
  Object.entries(summaryObject)
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

const clickedButtonEntries = computed(() =>
  sortedEntries(clickedButtonSummary.value).map(([title, count]) => ({
    label: title || t('CAMPAIGN.SENDS.BUTTON_CLICKS.UNKNOWN'),
    count,
  }))
);

const failedErrorEntries = computed(() =>
  sortedEntries(failedErrorSummary.value).map(([code, count]) => ({
    label:
      translatedDeliveryError(code === 'null' ? null : code) ||
      t('CAMPAIGN.SENDS.ERROR_BREAKDOWN.UNKNOWN'),
    count,
  }))
);

// Gated on the server's own count of retryable rows, not on the delivery-failure count: a send
// with no message behind it shows as a delivery failure but has nothing for Messages::RetryService
// to re-send, and a button that starts a run picking up zero rows is indistinguishable from a bug.
const canRetry = computed(() => retryableCount.value > 0);

const retryBatch = computed(() =>
  Math.min(retryableCount.value, RETRY_BATCH_SIZE)
);

// Everything that failed but cannot be retried from here: sends that broke before a message
// existed, plus delivery failures whose message is gone.
const nonRetryableCount = computed(
  () =>
    (summary.value.failed || 0) +
    Math.max(0, deliveryFailedCount.value - retryableCount.value)
);

const fetchSends = async page => {
  if (!props.campaign?.id) return;
  isLoading.value = true;
  try {
    const data = await store.dispatch('campaigns/getCampaignSends', {
      campaignId: props.campaign.id,
      page,
      status: statusFilter.value,
    });
    sends.value = data.campaign_sends;
    meta.totalEntries = data.total_entries;
    meta.perPage = data.per_page;
    currentPage.value = data.current_page;
    summary.value = data.summary || {};
    pricingSummary.value = data.pricing_summary || {};
    engagementSummary.value = data.engagement_summary || {};
    failedErrorSummary.value = data.failed_error_summary || {};
    clickedButtonSummary.value = data.clicked_button_summary || {};
    deliveryFailedCount.value = data.delivery_failed_count || 0;
    retryableCount.value = data.retryable_count || 0;
  } catch (error) {
    useAlert(t('CAMPAIGN.SENDS.API.ERROR_MESSAGE'));
  } finally {
    isLoading.value = false;
  }
};

const confirmRetry = async () => {
  isRetrying.value = true;
  try {
    await store.dispatch('campaigns/retryFailedSends', {
      campaignId: props.campaign.id,
      limit: RETRY_BATCH_SIZE,
    });
    useAlert(t('CAMPAIGN.SENDS.RETRY.STARTED'));
    // The sends themselves are asynchronous, so this only refreshes what is already settled.
    await fetchSends(currentPage.value);
  } catch {
    useAlert(t('CAMPAIGN.SENDS.RETRY.ERROR'));
  } finally {
    isRetrying.value = false;
  }
};

const handlePageChange = page => fetchSends(page);

watch(statusFilter, () => fetchSends(1));

watch(
  () => props.campaign,
  campaign => {
    if (!campaign) return;
    statusFilter.value = '';
    sends.value = [];
    Object.assign(pricePerCategory, META_WHATSAPP_PRICE_USD);
    fetchSends(1);
  }
);

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('CAMPAIGN.SENDS.TITLE', { title: campaign?.title })"
    :show-cancel-button="false"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-6">
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div class="flex items-center gap-4 text-sm text-n-slate-11">
          <span>
            {{ t('CAMPAIGN.SENDS.SUMMARY.SENT') }}
            <strong class="text-n-slate-12">{{ summary.sent || 0 }}</strong>
          </span>
          <span>
            {{ t('CAMPAIGN.SENDS.SUMMARY.FAILED') }}
            <strong class="text-n-slate-12">{{ summary.failed || 0 }}</strong>
          </span>
          <span>
            {{ t('CAMPAIGN.SENDS.SUMMARY.SKIPPED') }}
            <strong class="text-n-slate-12">{{ summary.skipped || 0 }}</strong>
          </span>
          <span v-if="deliveryFailedCount">
            {{ t('CAMPAIGN.SENDS.SUMMARY.DELIVERY_FAILED') }}
            <strong class="text-n-ruby-11">{{ deliveryFailedCount }}</strong>
          </span>
        </div>
        <SegmentedControl v-model="statusFilter" :options="statusOptions" />
      </div>

      <div
        v-if="hasEngagementSummary"
        class="grid grid-cols-2 sm:grid-cols-4 gap-3"
      >
        <div
          v-for="metric in ['delivered', 'read', 'replied', 'clicked']"
          :key="metric"
          class="flex flex-col gap-0.5 p-3 rounded-xl bg-n-alpha-1"
        >
          <span class="text-xs text-n-slate-11">
            {{ t(`CAMPAIGN.SENDS.ENGAGEMENT.${metric.toUpperCase()}`) }}
          </span>
          <span class="text-lg font-medium text-n-slate-12">
            {{ engagementSummary[metric] || 0 }}
            <span class="text-xs font-normal text-n-slate-11">
              ({{ engagementRate(metric) }}%)
            </span>
          </span>
        </div>
      </div>

      <div v-if="clickedButtonEntries.length" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.SENDS.BUTTON_CLICKS.TITLE') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="entry in clickedButtonEntries"
            :key="entry.label"
            class="text-xs px-2 py-1 rounded-md bg-n-alpha-1 text-n-slate-11"
          >
            {{ entry.label }} ·
            <strong class="text-n-slate-12">{{ entry.count }}</strong>
          </span>
        </div>
      </div>

      <div v-if="failedErrorEntries.length" class="flex flex-col gap-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.SENDS.ERROR_BREAKDOWN.TITLE') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="entry in failedErrorEntries"
            :key="entry.label"
            class="text-xs px-2 py-1 rounded-md bg-n-ruby-2 text-n-ruby-11"
          >
            {{ entry.label }} · <strong>{{ entry.count }}</strong>
          </span>
        </div>
      </div>

      <div
        v-if="canRetry"
        class="flex flex-wrap items-center justify-between gap-3 p-3 border rounded-lg border-n-weak"
      >
        <div class="flex flex-col gap-0.5">
          <span class="text-sm text-n-slate-12">
            {{
              t(
                'CAMPAIGN.SENDS.RETRY.AVAILABLE',
                { count: retryableCount },
                retryableCount
              )
            }}
          </span>
          <span v-if="nonRetryableCount" class="text-xs text-n-slate-11">
            {{
              t(
                'CAMPAIGN.SENDS.RETRY.NOT_RETRYABLE',
                { count: nonRetryableCount },
                nonRetryableCount
              )
            }}
          </span>
        </div>
        <Button
          sm
          :label="t('CAMPAIGN.SENDS.RETRY.BUTTON')"
          :is-loading="isRetrying"
          :disabled="isRetrying"
          @click="retryDialogRef.open()"
        />
      </div>

      <Dialog
        ref="retryDialogRef"
        type="alert"
        :title="t('CAMPAIGN.SENDS.RETRY.CONFIRM.TITLE')"
        :confirm-button-label="t('CAMPAIGN.SENDS.RETRY.CONFIRM.CONFIRM')"
        :cancel-button-label="t('CAMPAIGN.SENDS.RETRY.CONFIRM.CANCEL')"
        :is-loading="isRetrying"
        @confirm="confirmRetry"
      >
        <div class="flex flex-col gap-2 text-sm">
          <p class="text-n-slate-12">
            {{ t('CAMPAIGN.SENDS.RETRY.CONFIRM.BODY') }}
          </p>
          <p class="text-n-slate-11">
            {{
              retryBatch < retryableCount
                ? t('CAMPAIGN.SENDS.RETRY.CONFIRM.BATCH', {
                    count: retryBatch,
                    total: retryableCount,
                  })
                : t(
                    'CAMPAIGN.SENDS.RETRY.CONFIRM.BATCH_ALL',
                    { count: retryBatch },
                    retryBatch
                  )
            }}
          </p>
        </div>
      </Dialog>

      <div v-if="isLoading" class="flex items-center justify-center py-10">
        <Spinner />
      </div>
      <div v-else class="flex flex-col">
        <BaseTable
          :headers="tableHeaders"
          :items="sends"
          :no-data-message="t('CAMPAIGN.SENDS.TABLE.NO_DATA')"
        >
          <template #row="{ items }">
            <BaseTableRow v-for="send in items" :key="send.id" :item="send">
              <template #default>
                <BaseTableCell>
                  <a
                    v-if="send.conversation_display_id"
                    :href="conversationUrl(send.conversation_display_id)"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-n-blue-11 hover:underline"
                  >
                    {{ send.contact?.name || '---' }}
                  </a>
                  <span v-else class="text-n-slate-12">
                    {{ send.contact?.name || '---' }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="whitespace-nowrap">
                    {{ send.phone_number }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
                    :class="statusBadgeClass(rowStatus(send))"
                  >
                    {{ rowStatusLabel(send) }}
                  </span>
                </BaseTableCell>
                <BaseTableCell>
                  <span
                    v-if="rowError(send)"
                    class="line-clamp-1 max-w-52"
                    :title="rowError(send)"
                  >
                    {{ rowError(send) }}
                  </span>
                  <span v-else>---</span>
                </BaseTableCell>
                <BaseTableCell>
                  <span class="whitespace-nowrap">
                    {{ standardDateTime(send.created_at) }}
                  </span>
                </BaseTableCell>
              </template>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          v-if="meta.totalEntries > meta.perPage"
          :current-page="Number(currentPage)"
          :total-items="meta.totalEntries"
          :items-per-page="meta.perPage"
          class="!px-0"
          @update:current-page="handlePageChange"
        />
      </div>

      <div
        v-if="hasPricingSummary"
        class="flex flex-col gap-3 pt-2 border-t border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.SENDS.COST.TITLE') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.SENDS.COST.DESCRIPTION') }}
            <a
              href="https://developers.facebook.com/documentation/business-messaging/whatsapp/pricing"
              target="_blank"
              rel="noopener noreferrer"
              class="text-n-blue-11 hover:underline"
            >
              {{ t('CAMPAIGN.SENDS.COST.SOURCE_LINK') }}
            </a>
          </span>
        </div>
        <div
          v-for="category in pricingCategories"
          :key="category"
          class="flex items-center justify-between gap-3"
        >
          <span class="text-sm text-n-slate-11 min-w-0 flex-1">
            {{ t(`CAMPAIGN.SENDS.COST.CATEGORY.${category.toUpperCase()}`) }}
            <span class="text-n-slate-12"
              >({{ pricingSummary[category] }})</span
            >
          </span>
          <div class="flex items-center gap-1">
            <span class="text-sm text-n-slate-11">
              {{ t('CAMPAIGN.SENDS.COST.CURRENCY_PREFIX') }}
            </span>
            <Input
              v-model="pricePerCategory[category]"
              type="number"
              min="0"
              step="0.0001"
              size="sm"
              custom-input-class="!w-24"
              :placeholder="t('CAMPAIGN.SENDS.COST.PRICE_PLACEHOLDER')"
            />
          </div>
        </div>
        <div
          class="flex items-center justify-between pt-2 border-t border-n-weak"
        >
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.SENDS.COST.TOTAL') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.SENDS.COST.CURRENCY_PREFIX') }}
            {{ estimatedTotal.toFixed(2) }}
          </span>
        </div>
      </div>
    </div>
  </Dialog>
</template>
